/**
 * Wraps an AnalyserNode so a playback graph can be metered without every caller
 * re-deriving the dBFS maths.
 *
 * Both playback engines splice one of these in after their master `drumsetVolume`
 * gain, so what it reports is what is actually heard: master volume changes and
 * mutes move the meter.
 */

/** Bottom of the meter scale. Anything quieter than this reads as silence. */
export const METER_FLOOR_DB = -60;

export interface Level {
  peakDb: number;
  rmsDb: number;
}

export const SILENCE: Level = { peakDb: METER_FLOOR_DB, rmsDb: METER_FLOOR_DB };

export function amplitudeToDb(amplitude: number) {
  if (amplitude <= 0) {
    return METER_FLOOR_DB;
  }
  // Not clamped at the top: samples can exceed unity when trims add gain, and
  // the meter needs to see that to flag clipping.
  return Math.max(METER_FLOOR_DB, 20 * Math.log10(amplitude));
}

export default class LevelAnalyser {
  readonly node: AnalyserNode;
  #audioContext: BaseAudioContext;
  #samples: Float32Array<ArrayBuffer>;

  constructor(audioContext: BaseAudioContext) {
    this.#audioContext = audioContext;
    this.node = new AnalyserNode(audioContext, {
      fftSize: 1024,
      smoothingTimeConstant: 0.8,
    });
    this.#samples = new Float32Array(this.node.fftSize);
  }

  /** Splice this analyser between an existing node and its destination. */
  insertBetween(source: AudioNode, destination: AudioNode) {
    source.connect(this.node);
    this.node.connect(destination);
  }

  read(): Level {
    // The contexts start suspended and are only resumed on play. A suspended
    // analyser keeps handing back whatever was last heard, so report silence
    // rather than letting the meter freeze mid-bounce.
    if (this.#audioContext.state !== 'running') {
      return SILENCE;
    }

    this.node.getFloatTimeDomainData(this.#samples);

    let peak = 0;
    let sumOfSquares = 0;
    for (const sample of this.#samples) {
      const magnitude = Math.abs(sample);
      if (magnitude > peak) {
        peak = magnitude;
      }
      sumOfSquares += sample * sample;
    }

    return {
      peakDb: amplitudeToDb(peak),
      rmsDb: amplitudeToDb(Math.sqrt(sumOfSquares / this.#samples.length)),
    };
  }
}
